'use client'

import React, { useRef, useEffect } from 'react'
import { Box, Modal, UnstyledButton } from '@mantine/core'

interface VideoPlayerProps {
    opened: boolean
    onClose: () => void
    videoUrl: string | null
    isSm: boolean
    zIndex?: number
}

export default function VideoPlayer({ opened, onClose, videoUrl, isSm, zIndex = 11 }: VideoPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null)

    useEffect(() => {
        const video = videoRef.current
        if (!video || !opened || !videoUrl) return

        // Reset and play video when modal opens
        video.load()
        const playPromise = video.play()

        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                console.error('Error playing video:', error)
            })
        }

        // Pause video when modal closes
        return () => {
            if (video) {
                video.pause()
                video.currentTime = 0
            }
        }
    }, [opened, videoUrl])

    if (!videoUrl) {
        return null
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            fullScreen
            withCloseButton={false}
            zIndex={zIndex}
            styles={{
                body: {
                    padding: 26,
                    paddingBottom: 160,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                },
                content: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: 13,
                    height: '100%',
                    maxHeight: '100%',
                },
                inner: {
                    padding: 0,
                },
                overlay: {
                    zIndex: zIndex,
                },
            }}
        >
            <Box
                pos="relative"
                w="100%"
                h="100%"
                style={{
                    borderRadius: 24,
                    overflow: 'hidden',
                }}
            >
                <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    autoPlay
                    playsInline
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                    }}
                />
            </Box>
            <UnstyledButton
                onClick={onClose}
                aria-label="Close video"
                pos="absolute"
                top={isSm ? 40 : 67}
                right={isSm ? 40 : 67}
                w={isSm ? 56 : 72}
                h={isSm ? 56 : 72}
                style={{
                    borderRadius: '50%',
                    background: '#0066cc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                    color: '#ffffff',
                }}
            >
                <svg
                    width={isSm ? 32 : 40}
                    height={isSm ? 32 : 40}
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M40 24L24 40M24 24L40 40"
                        stroke="#FFFFFF"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </UnstyledButton>
        </Modal>
    )
}
